/**
 * Nurse Phase C — Workflow Extension components
 *
 * Bundled in one file so the Nurse Workstation page can simply import {ShiftHandoverPanel, ChecklistsPanel,
 * EmergencyPanel, FloatingEmergencyButton}. Every form is dropdown-first to minimise typing.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { nurseAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Check, AlertTriangle, Stethoscope, BedDouble, Bell, Heart, Phone, Activity, Siren, ArrowRightCircle, FileText, X as XIcon, Clipboard } from 'lucide-react';
import { cn } from '../lib/utils';

// ========== DROPDOWN OPTION SETS (the heart of "no typing") ==========
export const PATIENT_CONDITIONS = [
  { value: 'stable', label: 'Stable' },
  { value: 'improving', label: 'Improving' },
  { value: 'deteriorating', label: 'Deteriorating' },
  { value: 'critical', label: 'Critical' },
  { value: 'pre_op', label: 'Pre-op' },
  { value: 'post_op', label: 'Post-op' },
  { value: 'recovering', label: 'Recovering' },
];

export const PENDING_ACTIONS = [
  { value: 'vitals_check', label: 'Vitals check' },
  { value: 'med_rounds', label: 'Medication rounds' },
  { value: 'sample_collection', label: 'Lab sample collection' },
  { value: 'dressing', label: 'Dressing change' },
  { value: 'discharge_prep', label: 'Discharge prep' },
  { value: 'iv_change', label: 'IV change' },
  { value: 'catheter_care', label: 'Catheter care' },
  { value: 'physio', label: 'Physiotherapy assist' },
  { value: 'diet_review', label: 'Diet review' },
];

export const ESCALATIONS = [
  { value: 'none', label: 'None' },
  { value: 'doctor_called', label: 'Doctor called' },
  { value: 'doctor_attending', label: 'Doctor attending' },
  { value: 'srn_escalated', label: 'Senior Nurse escalated' },
  { value: 'family_informed', label: 'Family informed' },
  { value: 'icu_consult', label: 'ICU consult requested' },
];

export const OXYGEN_SUPPORT = [
  { value: 'none', label: 'Room air' },
  { value: 'nasal_cannula_2l', label: 'Nasal cannula 2L/min' },
  { value: 'nasal_cannula_4l', label: 'Nasal cannula 4L/min' },
  { value: 'face_mask_5l', label: 'Face mask 5L/min' },
  { value: 'face_mask_8l', label: 'Face mask 8L/min' },
  { value: 'nrbm', label: 'Non-rebreather mask' },
  { value: 'bipap', label: 'BiPAP' },
  { value: 'cpap', label: 'CPAP' },
  { value: 'ventilator', label: 'Mechanical ventilator' },
];

export const ICU_ALERTS = [
  { value: 'none', label: 'No concern' },
  { value: 'watch', label: 'Watch closely' },
  { value: 'consider_transfer', label: 'Consider ICU transfer' },
  { value: 'transfer_requested', label: 'Transfer requested' },
  { value: 'transferred', label: 'Already transferred' },
];

export const SHIFT_OPTIONS = [
  { value: 'morning', label: 'Morning (07:00 – 14:00)' },
  { value: 'afternoon', label: 'Afternoon (14:00 – 21:00)' },
  { value: 'night', label: 'Night (21:00 – 07:00)' },
];

export const NPO_OPTIONS = [
  { value: 'regular_diet', label: 'Regular diet' },
  { value: 'soft_diet', label: 'Soft diet' },
  { value: 'clear_liquids', label: 'Clear liquids only' },
  { value: 'npo', label: 'NPO (nil by mouth)' },
  { value: 'diabetic_diet', label: 'Diabetic diet' },
];

export const FOLLOW_UP_OPTIONS = [
  { value: '3_days', label: 'In 3 days' },
  { value: '1_week', label: 'In 1 week' },
  { value: '2_weeks', label: 'In 2 weeks' },
  { value: '1_month', label: 'In 1 month' },
  { value: '3_months', label: 'In 3 months' },
  { value: 'as_needed', label: 'As needed (SOS)' },
];

export const UNDERSTANDING_OPTIONS = [
  { value: 'good', label: 'Good — patient understood' },
  { value: 'needs_reinforcement', label: 'Needs reinforcement' },
  { value: 'family_explained', label: 'Family explained on behalf' },
  { value: 'language_barrier', label: 'Language barrier — translator used' },
];

export const CODE_BLUE_REASONS = [
  'Cardiac arrest', 'Respiratory arrest', 'Unresponsive', 'Severe bradycardia',
  'Severe tachycardia', 'Seizure with apnea', 'Other — see notes',
];

export const ICU_DESTINATIONS = [
  { value: 'MICU', label: 'Medical ICU (MICU)' },
  { value: 'SICU', label: 'Surgical ICU (SICU)' },
  { value: 'CCU', label: 'Coronary Care Unit (CCU)' },
  { value: 'PICU', label: 'Paediatric ICU (PICU)' },
  { value: 'NICU', label: 'Neonatal ICU (NICU)' },
  { value: 'HDU', label: 'High Dependency Unit (HDU)' },
];

export const ICU_TRANSFER_REASONS = [
  'Hemodynamic instability', 'Respiratory distress', 'Post-op monitoring',
  'Sepsis / Septic shock', 'Neurological deterioration', 'Multi-organ support',
];

export const DOCTOR_ALERT_REASONS = [
  'Patient deteriorating', 'BP not controlled', 'High fever > 39°C',
  'SpO₂ dropping', 'Severe pain', 'Family request',
  'Medication query', 'Lab abnormal', 'Other — see notes',
];

// ========== SHIFT HANDOVER PANEL ==========
export const ShiftHandoverPanel = ({ wardId, wardName }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await nurseAPI.getShiftHandover(wardId ? { ward_id: wardId } : {});
      setList(r.data || []);
    } catch (e) {
      toast.error('Failed to load handovers');
    } finally {
      setLoading(false);
    }
  }, [wardId]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing({
      shift: SHIFT_OPTIONS[0].value,
      patient_condition: 'stable',
      pending_actions: [],
      escalations: 'none',
      oxygen_support: 'none',
      icu_alert: 'none',
      remarks: '',
    });
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing({ ...row });
    setOpen(true);
  };

  const save = async () => {
    try {
      await nurseAPI.saveShiftHandover({ ...editing, ward_id: wardId });
      toast.success('Handover saved');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div className="space-y-4" data-testid="shift-handover-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900">Shift Handover {wardName ? `— ${wardName}` : ''}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Today's handover notes from each shift. Tap to start or update.</p>
        </div>
        <Button onClick={startNew} className="bg-blue-600 hover:bg-blue-700 h-11 px-5" data-testid="new-handover-btn">
          <FileText size={16} className="mr-1.5" /> New / Update Handover
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {SHIFT_OPTIONS.map(s => {
            const h = list.find(r => r.shift === s.value);
            return (
              <button
                key={s.value}
                onClick={() => h ? startEdit(h) : (setEditing({ shift: s.value, patient_condition: 'stable', pending_actions: [], escalations: 'none', oxygen_support: 'none', icu_alert: 'none', remarks: '' }), setOpen(true))}
                className={cn(
                  'text-left rounded-xl border p-4 transition shadow-sm hover:shadow-md',
                  h ? 'bg-white border-emerald-200' : 'bg-slate-50 border-dashed border-slate-300',
                )}
                data-testid={`handover-card-${s.value}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-800">{s.label.split(' (')[0]}</p>
                  {h ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Recorded</Badge> : <Badge className="bg-slate-200 text-slate-600 text-[10px]">Pending</Badge>}
                </div>
                {h ? (
                  <div className="space-y-1 text-xs text-slate-600">
                    <p>Condition: <span className="font-semibold">{PATIENT_CONDITIONS.find(c => c.value === h.patient_condition)?.label || h.patient_condition}</span></p>
                    <p>Oxygen: <span className="font-semibold">{OXYGEN_SUPPORT.find(c => c.value === h.oxygen_support)?.label || h.oxygen_support}</span></p>
                    <p>ICU: <span className="font-semibold">{ICU_ALERTS.find(c => c.value === h.icu_alert)?.label || h.icu_alert}</span></p>
                    {h.pending_actions?.length > 0 && (
                      <p className="text-[11px] text-slate-500 mt-1">{h.pending_actions.length} pending action{h.pending_actions.length > 1 ? 's' : ''}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1.5">by {h.updated_by_name} · {h.updated_at?.slice(11, 16)}</p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">Tap to record handover</p>
                )}
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Shift Handover</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <DropdownField label="Shift" testId="hover-shift" value={editing.shift} onChange={(v) => setEditing(e => ({ ...e, shift: v }))} options={SHIFT_OPTIONS} />
                <DropdownField label="Patient condition" testId="hover-condition" value={editing.patient_condition} onChange={(v) => setEditing(e => ({ ...e, patient_condition: v }))} options={PATIENT_CONDITIONS} />
                <DropdownField label="Escalations" testId="hover-escalations" value={editing.escalations} onChange={(v) => setEditing(e => ({ ...e, escalations: v }))} options={ESCALATIONS} />
                <DropdownField label="Oxygen support" testId="hover-oxygen" value={editing.oxygen_support} onChange={(v) => setEditing(e => ({ ...e, oxygen_support: v }))} options={OXYGEN_SUPPORT} />
                <DropdownField label="ICU alert" testId="hover-icu" value={editing.icu_alert} onChange={(v) => setEditing(e => ({ ...e, icu_alert: v }))} options={ICU_ALERTS} />
              </div>
              <ChipMultiSelect
                label="Pending actions for next shift"
                testId="hover-pending"
                values={editing.pending_actions || []}
                onChange={(vals) => setEditing(e => ({ ...e, pending_actions: vals }))}
                options={PENDING_ACTIONS}
              />
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Remarks (optional)</Label>
                <Textarea rows={3} value={editing.remarks || ''} onChange={(e) => setEditing(s => ({ ...s, remarks: e.target.value }))} placeholder="Anything that isn't captured above…" data-testid="hover-remarks" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-11 px-5">Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5" data-testid="hover-save">Save Handover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== HELPERS — Dropdown & chip multi-select ==========
const DropdownField = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid={testId}>
        <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const ChipMultiSelect = ({ label, values, onChange, options, testId }) => {
  const toggle = (v) => onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]);
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testId}>
        {options.map(o => {
          const on = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className={cn(
                'px-3 py-1.5 rounded-lg border text-sm font-medium transition',
                on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50',
              )}
              data-testid={`${testId}-opt-${o.value}`}
            >
              {on && <Check size={12} className="inline-block mr-1" />}{o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ========== CHECKLISTS PANEL (admission + discharge) ==========
export const ChecklistsPanel = ({ patients, onRefresh }) => {
  const [tab, setTab] = useState('admission'); // admission | discharge
  const [activeAdm, setActiveAdm] = useState(null);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const startChecklist = async (kind, patient) => {
    setActiveAdm({ kind, patient });
    try {
      const fn = kind === 'admission' ? nurseAPI.getAdmissionChecklist : nurseAPI.getDischargeChecklist;
      const r = await fn(patient.admission_id);
      setData(r.data);
      setOpen(true);
    } catch (e) {
      toast.error('Failed to load checklist');
    }
  };

  const save = async () => {
    if (!activeAdm) return;
    try {
      const fn = activeAdm.kind === 'admission' ? nurseAPI.saveAdmissionChecklist : nurseAPI.saveDischargeChecklist;
      await fn(activeAdm.patient.admission_id, data);
      toast.success(`${activeAdm.kind === 'admission' ? 'Admission' : 'Discharge'} checklist saved`);
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const toggleItem = (key) => {
    setData(d => ({ ...d, items: d.items.map(i => i.key === key ? { ...i, done: !i.done } : i) }));
  };

  return (
    <div className="space-y-4" data-testid="checklists-panel">
      <div className="flex items-center gap-2">
        <button onClick={() => setTab('admission')} className={cn('px-4 py-2 rounded-lg text-sm font-semibold border transition', tab === 'admission' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50')} data-testid="checklist-tab-admission">
          <BedDouble size={14} className="inline-block mr-1.5 -mt-0.5" /> Admission Checklists
        </button>
        <button onClick={() => setTab('discharge')} className={cn('px-4 py-2 rounded-lg text-sm font-semibold border transition', tab === 'discharge' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50')} data-testid="checklist-tab-discharge">
          <ArrowRightCircle size={14} className="inline-block mr-1.5 -mt-0.5" /> Discharge Checklists
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {patients.map(p => (
          <button
            key={p.patient_id}
            onClick={() => startChecklist(tab, p)}
            className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition"
            data-testid={`checklist-patient-${p.patient_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-slate-800">{p.patient_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">Bed {p.bed_number || '—'} · UHID {p.uhid || '—'}</p>
                <p className="text-xs text-slate-400 mt-0.5">{p.diagnosis || 'No diagnosis recorded'}</p>
              </div>
              <Clipboard size={18} className="text-slate-400 flex-shrink-0" />
            </div>
            <p className="text-[10px] text-blue-600 mt-2 font-semibold">{tab === 'admission' ? 'Open admission checklist →' : 'Open discharge checklist →'}</p>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeAdm?.kind === 'admission' ? 'Admission' : 'Discharge'} Checklist — {activeAdm?.patient?.patient_name}
            </DialogTitle>
            <p className="text-xs text-slate-500">Bed {activeAdm?.patient?.bed_number || '—'} · UHID {activeAdm?.patient?.uhid || '—'}</p>
          </DialogHeader>
          {data && (
            <div className="space-y-3 py-2">
              {/* Checklist items as big toggle rows */}
              <div className="space-y-2">
                {data.items.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleItem(item.key)}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-lg border-2 transition text-left',
                      item.done ? 'bg-emerald-50 border-emerald-400 text-emerald-800' : 'bg-white border-slate-200 hover:border-slate-300',
                    )}
                    data-testid={`checklist-item-${item.key}`}
                  >
                    <span className={cn('h-7 w-7 rounded flex items-center justify-center border-2 flex-shrink-0', item.done ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300')}>
                      {item.done && <Check size={16} className="text-white" />}
                    </span>
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Kind-specific dropdowns */}
              {activeAdm?.kind === 'admission' ? (
                <DropdownField
                  label="Diet / NPO status"
                  testId="checklist-npo"
                  value={data.npo_status || 'regular_diet'}
                  onChange={(v) => setData(d => ({ ...d, npo_status: v }))}
                  options={NPO_OPTIONS}
                />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <DropdownField
                    label="Follow-up scheduled"
                    testId="checklist-followup"
                    value={data.follow_up_in || '1_week'}
                    onChange={(v) => setData(d => ({ ...d, follow_up_in: v }))}
                    options={FOLLOW_UP_OPTIONS}
                  />
                  <DropdownField
                    label="Patient understanding"
                    testId="checklist-understanding"
                    value={data.patient_understanding || 'good'}
                    onChange={(v) => setData(d => ({ ...d, patient_understanding: v }))}
                    options={UNDERSTANDING_OPTIONS}
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-11 px-5">Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5" data-testid="checklist-save">
              Save Checklist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== EMERGENCY PANEL + FLOATING BUTTON ==========
// Hospital code-colour conventions:
//   Code Blue   → BLUE     (cardiac/respiratory arrest)
//   ICU Transfer → ORANGE  (urgent escalation)
//   Doctor Alert → TEAL    (notify treating doctor)
//   Rapid Response → AMBER (deteriorating patient — pre-arrest)
const EVENT_META = {
  code_blue: { label: 'CODE BLUE', icon: Heart, color: 'bg-blue-600', hoverColor: 'hover:bg-blue-700', subtitle: 'Cardiac/respiratory arrest' },
  icu_transfer: { label: 'ICU Transfer', icon: ArrowRightCircle, color: 'bg-orange-500', hoverColor: 'hover:bg-orange-600', subtitle: 'Request transfer to ICU' },
  doctor_alert: { label: 'Doctor Alert', icon: Phone, color: 'bg-teal-600', hoverColor: 'hover:bg-teal-700', subtitle: 'Notify treating doctor' },
  rapid_response: { label: 'Rapid Response', icon: Siren, color: 'bg-amber-500', hoverColor: 'hover:bg-amber-600', subtitle: 'Deteriorating patient' },
};

export const EmergencyPanel = ({ patients, onRefresh, doctorsForAlert = [], wardsForTransfer = [] }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('code_blue');
  const [form, setForm] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await nurseAPI.listEmergencies({ days: 7 });
      setEvents(r.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openFor = (t) => {
    setType(t);
    setForm({});
    setOpen(true);
  };

  const trigger = async () => {
    try {
      const p = patients.find(pp => pp.patient_id === form.patient_id);
      await nurseAPI.triggerEmergency({
        event_type: type,
        patient_id: form.patient_id || null,
        bed_number: p?.bed_number,
        ward_id: p?.ward_id,
        destination_ward_type: form.destination_ward_type,
        target_doctor_id: form.target_doctor_id,
        target_doctor_name: doctorsForAlert.find(d => d.id === form.target_doctor_id)?.name,
        reason: form.reason,
        notes: form.notes,
      });
      toast.success(`${EVENT_META[type].label} alert sent`);
      setOpen(false);
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to trigger emergency');
    }
  };

  const resolve = async (ev) => {
    try {
      await nurseAPI.resolveEmergency(ev.id, '');
      toast.success('Marked as resolved');
      load();
    } catch (e) {
      toast.error('Failed to resolve');
    }
  };

  const reasonOptions =
    type === 'code_blue' ? CODE_BLUE_REASONS :
    type === 'icu_transfer' ? ICU_TRANSFER_REASONS :
    DOCTOR_ALERT_REASONS;

  return (
    <div className="space-y-5" data-testid="emergency-panel">
      {/* Big action buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(EVENT_META).map(([k, m]) => (
          <button
            key={k}
            onClick={() => openFor(k)}
            className={cn('rounded-xl p-5 text-white text-left shadow-md hover:shadow-lg transition active:scale-[0.98]', m.color, m.hoverColor)}
            data-testid={`emergency-action-${k}`}
          >
            <m.icon size={24} className="mb-2" />
            <p className="text-base font-bold">{m.label}</p>
            <p className="text-xs opacity-90 mt-0.5">{m.subtitle}</p>
          </button>
        ))}
      </div>

      {/* Active events */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5"><AlertTriangle size={14} /> Recent Emergency Events (7 days)</h3>
        {loading ? (
          <div className="h-24 bg-slate-100 rounded-lg animate-pulse" />
        ) : events.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-4 text-center">No emergency events in the last 7 days. Stay safe!</p>
        ) : (
          <div className="space-y-2">
            {events.map(ev => {
              const m = EVENT_META[ev.event_type] || EVENT_META.doctor_alert;
              return (
                <div key={ev.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`emergency-event-${ev.id}`}>
                  <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center text-white', m.color)}>
                    <m.icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{m.label}</p>
                    <p className="text-xs text-slate-500 truncate">{ev.reason} {ev.bed_number ? `· Bed ${ev.bed_number}` : ''} · by {ev.created_by_name}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{ev.created_at?.slice(0, 16).replace('T', ' ')}</p>
                  </div>
                  <Badge className={cn('text-[10px]', ev.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>{ev.status}</Badge>
                  {ev.status !== 'resolved' && (
                    <Button onClick={() => resolve(ev)} size="sm" variant="outline" className="h-9 text-xs" data-testid={`emergency-resolve-${ev.id}`}>Resolve</Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {EVENT_META[type] && (() => {
                const Ic = EVENT_META[type].icon;
                // Match the icon colour to the code-colour convention (Blue for Code Blue, etc.)
                const iconColour = type === 'code_blue' ? 'text-blue-600' : type === 'icu_transfer' ? 'text-orange-500' : type === 'doctor_alert' ? 'text-teal-600' : 'text-amber-500';
                return <Ic size={20} className={iconColour} />;
              })()}
              {EVENT_META[type]?.label}
            </DialogTitle>
            <p className="text-xs text-slate-500">{EVENT_META[type]?.subtitle}</p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Patient picker */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Patient</Label>
              <Select value={form.patient_id || ''} onValueChange={(v) => setForm(f => ({ ...f, patient_id: v }))}>
                <SelectTrigger className="h-11" data-testid="emergency-patient">
                  <SelectValue placeholder="Select patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map(p => <SelectItem key={p.patient_id} value={p.patient_id}>{p.patient_name} — Bed {p.bed_number || '—'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Reason — dropdown chips so no typing */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Reason</Label>
              <div className="flex flex-wrap gap-2" data-testid="emergency-reason">
                {reasonOptions.map(r => {
                  const on = form.reason === r;
                  // Selected chip mirrors the event-type colour (Code Blue → blue, etc.)
                  const selectedClass =
                    type === 'code_blue' ? 'bg-blue-600 text-white border-blue-600' :
                    type === 'icu_transfer' ? 'bg-orange-500 text-white border-orange-500' :
                    type === 'doctor_alert' ? 'bg-teal-600 text-white border-teal-600' :
                    'bg-amber-500 text-white border-amber-500';
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, reason: r }))}
                      className={cn('px-3 py-1.5 rounded-lg border text-sm font-medium transition', on ? selectedClass : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')}
                      data-testid={`emergency-reason-${r.toLowerCase().slice(0, 12).replace(/[^a-z]/g, '')}`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>

            {type === 'icu_transfer' && (
              <DropdownField
                label="Destination ICU"
                testId="emergency-destination"
                value={form.destination_ward_type || ''}
                onChange={(v) => setForm(f => ({ ...f, destination_ward_type: v }))}
                options={ICU_DESTINATIONS}
              />
            )}

            {type === 'doctor_alert' && doctorsForAlert.length > 0 && (
              <DropdownField
                label="Notify which doctor"
                testId="emergency-doctor"
                value={form.target_doctor_id || ''}
                onChange={(v) => setForm(f => ({ ...f, target_doctor_id: v }))}
                options={doctorsForAlert.map(d => ({ value: d.id, label: d.name }))}
              />
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Additional notes (optional)</Label>
              <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Only if it adds something the dropdowns don't capture" data-testid="emergency-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-11 px-5">Cancel</Button>
            <Button onClick={trigger} disabled={!form.reason} className={cn('h-11 px-5 text-white', EVENT_META[type]?.color, EVENT_META[type]?.hoverColor)} data-testid="emergency-trigger">
              <Siren size={16} className="mr-1.5" /> Trigger {EVENT_META[type]?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Floating Code-Blue button — always one tap away. Blue per hospital code-colour convention.
export const FloatingEmergencyButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-6 right-6 z-40 h-16 w-16 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-2xl flex flex-col items-center justify-center transition active:scale-95 ring-4 ring-blue-200"
    title="Emergency / Code Blue"
    data-testid="floating-emergency"
  >
    <Heart size={20} className="mb-0.5" />
    <span className="text-[9px] font-bold tracking-wider">CODE</span>
  </button>
);
